import { useState, useEffect } from 'react';
import classNames from 'classnames/bind';
import Link from 'next/link';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { NavigationMenu } from '../../components';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import ClientOnly from '../ClientOnly';
import styles from './Header.module.scss';
import { useQuery, useLazyQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import { flatListToHierarchical } from '@faustwp/core';
import useThemeStore from '../../store/themeStore';
import { getOptimizedImageUrl } from '../../utils/imageUtils';

let cx = classNames.bind(styles);

// Query for header data
const GET_HEADER_DATA = gql`
  query GetHeaderData($headerLocation: MenuLocationEnum, $language: LanguageCodeFilterEnum!) {
    headerMenuItems: menuItems(where: { location: $headerLocation, language: $language }) {
      nodes {
        id
        label
        path
        parentId
        childItems {
          nodes {
            id
            label
            path
          }
        }
      }
    }
    scoutOptionsPage {
      scoutSettings {
        logo {
          node {
            mediaItemUrl
            altText
          }
        }
        logoDarkmode {
          node {
            mediaItemUrl
            altText
          }
        }
      }
    }
  }
`;


export default function Header({
  menuItems: initialMenuItems,
  logo: initialLogo,
  logoDarkmode: initialLogoDarkmode,
}) {
  const router = useRouter();
  const { locale: activeLocale, locales, asPath } = router;
  const [isNavShown, setIsNavShown] = useState(false);
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);
  
  // Use the Zustand store for theme state
  const { isDark } = useThemeStore();

  // Query for header data with new language
  const { data: headerData, refetch } = useQuery(GET_HEADER_DATA, {
    variables: {
      headerLocation: "PRIMARY",
      language: activeLocale?.toUpperCase() === 'NB' ? 'NB' : 'EN'
    },
    // Start with initial data to prevent flash
    initialData: {
      headerMenuItems: { nodes: initialMenuItems },
      scoutOptionsPage: {
        scoutSettings: {
          logo: { node: initialLogo },
          logoDarkmode: { node: initialLogoDarkmode }
        }
      }
    }
  });

  
  // Lazy query for getting blog post translations when needed
  const GET_POST_TRANSLATIONS = gql`
    query GetPostTranslations($slug: ID!) {
      post(id: $slug, idType: SLUG) {
        slug
        uri
        language {
          code
        }
        translations {
          slug
          uri
          language {
            code
          }
        }
      }
    }
  `;
  const [getPostTranslations] = useLazyQuery(GET_POST_TRANSLATIONS);

  const menuItems = headerData?.headerMenuItems?.nodes || initialMenuItems;
  const logo = headerData?.scoutOptionsPage?.scoutSettings?.logo?.node || initialLogo;
  const logoDarkmode = headerData?.scoutOptionsPage?.scoutSettings?.logoDarkmode?.node || initialLogoDarkmode;

  useEffect(() => {
    // Prevent scrolling when mobile menu is open
    if (isNavShown) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isNavShown]);

  // Handle route changes
  useEffect(() => {
    if (isChangingLanguage) {
      setIsChangingLanguage(false);
    }
  }, [router.locale]);

  // Use ClientOnly for theme-dependent logo to prevent hydration mismatch
  const [currentLogo, setCurrentLogo] = useState(logo); // Default to light logo
  
  useEffect(() => {
    // Update logo after hydration
    setCurrentLogo(isDark ? logoDarkmode : logo);
  }, [isDark, logo, logoDarkmode]);

  const handleItemToggle = (itemId) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const getArchivePath = (locale) => locale === 'en' ? '/offices' : '/kontorer';

  const handleLanguageSwitch = async () => {
    if (isChangingLanguage) return;
    
    setIsChangingLanguage(true);
    const newLocale = activeLocale === 'nb' ? 'en' : 'nb';
    // Remove locale prefix from asPath for comparison
    const path = asPath.replace(/^\/(en|nb)(?=\/)/, '');
    
    console.log('DEBUG: asPath =', asPath, 'path =', path);
    
    // Check if on archive page - handle both with and without locale prefix
    const isNorwegianArchive = asPath === '/kontorer' || asPath === '/kontorer/';
    const isEnglishArchive = asPath === '/en/offices' || asPath === '/en/offices/' || asPath === '/offices' || asPath === '/offices/';
    
    if (isNorwegianArchive || isEnglishArchive) {
      console.log('DEBUG: Archive page detected!');
      const targetPath = getArchivePath(newLocale);
      console.log('DEBUG: targetPath =', targetPath, 'newLocale =', newLocale);
      try {
        // Use router.push with locale parameter for proper Next.js i18n handling
        await router.push(targetPath, targetPath, { locale: newLocale });
      } finally {
        setIsChangingLanguage(false);
      }
      return;
    }
    
    // Check if on area page (allow optional trailing slash)
    const norwegianAreaMatch = path.match(/^\/kontorer\/til-leie\/([^\/]+)\/?$/);
    const englishAreaMatch = path.match(/^\/offices\/for-rent\/([^\/]+)\/?$/);
    
    if (norwegianAreaMatch && newLocale === 'en') {
      // Norwegian to English: /kontorer/til-leie/[område] -> /offices/for-rent/[area]
      const [, område] = norwegianAreaMatch;
      
      // Map Norwegian areas to English areas
      const areaMapping = {
        'sentrum': 'city-centre',
        'bjorvika': 'bjorvika',
        'skoyen': 'skoyen',
        'lysaker': 'lysaker',
        'majorstuen': 'majorstuen',
        'frogner': 'frogner',
        'vika-aker-brygge': 'vika-aker-brygge',
        'grunerlokka': 'grunerlokka',
        'solli-plass': 'solli-plass',
        'hasle-loren': 'hasle-loren',
        'helsfyr': 'helsfyr',
        'okern': 'okern',
        'bryn': 'bryn',
        'drammen': 'drammen',
        'asker': 'asker',
        'tonsberg': 'tonsberg',
        'nydalen': 'nydalen',
        'ski': 'ski',
        'alna': 'alna',
        'ensjo': 'ensjo',
        'fornebu': 'fornebu',
        'sandvika': 'sandvika',
        'tjuvholmen': 'tjuvholmen'
      };
      
      const englishArea = areaMapping[område] || område;
      const targetPath = `/offices/for-rent/${englishArea}/`;
      
      try {
        await router.push(targetPath, targetPath, { locale: newLocale });
      } finally {
        setIsChangingLanguage(false);
      }
      return;
    }
    
    // Check if on blog single post page and map kontoristen <-> insights with translated slug
    const norwegianPostMatch = path.match(/^\/kontoristen\/([^\/]+)\/?$/);
    const englishPostMatch = path.match(/^\/insights\/([^\/]+)\/?$/);

    if (norwegianPostMatch && newLocale === 'en') {
      const [, slug] = norwegianPostMatch;
      try {
        const { data: translationsData } = await getPostTranslations({
          variables: { slug }
        });

        const post = translationsData?.post;
        let englishSlug = slug;
        if (post?.translations) {
          const englishTranslation = post.translations.find(t => t.language?.code?.toLowerCase() === 'en');
          if (englishTranslation?.slug) {
            englishSlug = englishTranslation.slug;
          }
        }

        const targetPath = `/insights/${englishSlug}`;
        await router.push(targetPath, targetPath, {
          locale: newLocale,
          scroll: false,
          shallow: false
        });
        await refetch({
          headerLocation: 'PRIMARY',
          language: newLocale.toUpperCase()
        });
        window.dispatchEvent(new CustomEvent('languageChange', { detail: { newLocale } }));
      } catch (error) {
        console.error('Error switching language for blog post:', error);
      } finally {
        setIsChangingLanguage(false);
      }
      return;
    }

    if (englishPostMatch && newLocale === 'nb') {
      const [, slug] = englishPostMatch;
      try {
        const { data: translationsData } = await getPostTranslations({
          variables: { slug }
        });

        const post = translationsData?.post;
        let norwegianSlug = slug;
        if (post?.translations) {
          const norwegianTranslation = post.translations.find(t => t.language?.code?.toLowerCase() === 'nb');
          if (norwegianTranslation?.slug) {
            norwegianSlug = norwegianTranslation.slug;
          }
        }

        const targetPath = `/kontoristen/${norwegianSlug}`;
        await router.push(targetPath, targetPath, {
          locale: newLocale,
          scroll: false,
          shallow: false
        });
        await refetch({
          headerLocation: 'PRIMARY',
          language: newLocale.toUpperCase()
        });
        window.dispatchEvent(new CustomEvent('languageChange', { detail: { newLocale } }));
      } catch (error) {
        console.error('Error switching language for blog post:', error);
      } finally {
        setIsChangingLanguage(false);
      }
      return;
    }
    
    if (englishAreaMatch && newLocale === 'nb') {
      // English to Norwegian: /offices/for-rent/[area] -> /kontorer/til-leie/[område]
      const [, area] = englishAreaMatch;
      
      // Map English areas to Norwegian areas
      const areaMapping = {
        'city-centre': 'sentrum',
        'bjorvika': 'bjorvika',
        'skoyen': 'skoyen',
        'lysaker': 'lysaker',
        'majorstuen': 'majorstuen',
        'frogner': 'frogner',
        'vika-aker-brygge': 'vika-aker-brygge',
        'grunerlokka': 'grunerlokka',
        'solli-plass': 'solli-plass',
        'hasle-loren': 'hasle-loren',
        'helsfyr': 'helsfyr',
        'okern': 'okern',
        'bryn': 'bryn',
        'drammen': 'drammen',
        'asker': 'asker',
        'tonsberg': 'tonsberg',
        'nydalen': 'nydalen',
        'ski': 'ski',
        'alna': 'alna',
        'ensjo': 'ensjo',
        'fornebu': 'fornebu',
        'sandvika': 'sandvika',
        'tjuvholmen': 'tjuvholmen'
      };
      
      const norwegianArea = areaMapping[area] || area;
      const targetPath = `/kontorer/til-leie/${norwegianArea}/`;
      
      try {
        await router.push(targetPath, targetPath, { locale: newLocale });
      } finally {
        setIsChangingLanguage(false);
      }
      return;
    }
    
    // Check if on new office page structure (capture slug without trailing slash)
    const norwegianOfficeMatch = path.match(/^\/kontorer\/til-leie\/([^\/]+)\/([^\/]+)\/?$/);
    const englishOfficeMatch = path.match(/^\/offices\/for-rent\/([^\/]+)\/([^\/]+)\/?$/);
    
    if (norwegianOfficeMatch && newLocale === 'en') {
      // Norwegian to English: /kontorer/til-leie/[område]/[slug] -> /en/offices/for-rent/[area]/[slug]
      const [, område, slug] = norwegianOfficeMatch;
      
      // Map Norwegian areas to English areas
      const areaMapping = {
        'sentrum': 'city-centre',
        'bjorvika': 'bjorvika',
        'skoyen': 'skoyen',
        'lysaker': 'lysaker',
        'majorstuen': 'majorstuen',
        'frogner': 'frogner',
        'vika-aker-brygge': 'vika-aker-brygge',
        'grunerlokka': 'grunerlokka',
        'solli-plass': 'solli-plass',
        'hasle-loren': 'hasle-loren',
        'helsfyr': 'helsfyr',
        'okern': 'okern',
        'bryn': 'bryn',
        'drammen': 'drammen',
        'asker': 'asker',
        'tonsberg': 'tonsberg',
        'nydalen': 'nydalen',
        'ski': 'ski',
        'alna': 'alna',
        'ensjo': 'ensjo',
        'fornebu': 'fornebu',
        'sandvika': 'sandvika',
        'tjuvholmen': 'tjuvholmen'
      };
      
      const englishArea = areaMapping[område] || område;
      const targetPath = `/en/offices/for-rent/${englishArea}/${slug}/`;
      
      console.log('Switching to English office (hard navigation):', { norwegianSlug: slug, targetPath });
      // Use hard navigation to ensure clean page load
      window.location.href = targetPath;
      setIsChangingLanguage(false);
      return;
    }
    
    if (englishOfficeMatch && newLocale === 'nb') {
      // English to Norwegian: /en/offices/for-rent/[area]/[slug] -> /kontorer/til-leie/[område]/[slug]
      const [, area, slug] = englishOfficeMatch;
      
      // Map English areas back to Norwegian areas
      const areaMapping = {
        'city-centre': 'sentrum',
        'bjorvika': 'bjorvika',
        'skoyen': 'skoyen',
        'lysaker': 'lysaker',
        'majorstuen': 'majorstuen',
        'frogner': 'frogner',
        'vika-aker-brygge': 'vika-aker-brygge',
        'grunerlokka': 'grunerlokka',
        'solli-plass': 'solli-plass',
        'hasle-loren': 'hasle-loren',
        'helsfyr': 'helsfyr',
        'okern': 'okern',
        'bryn': 'bryn',
        'drammen': 'drammen',
        'asker': 'asker',
        'tonsberg': 'tonsberg',
        'nydalen': 'nydalen',
        'ski': 'ski',
        'alna': 'alna',
        'ensjo': 'ensjo',
        'fornebu': 'fornebu',
        'sandvika': 'sandvika',
        'tjuvholmen': 'tjuvholmen'
      };
      
      const norwegianOmråde = areaMapping[area] || area;
      const targetPath = `/kontorer/til-leie/${norwegianOmråde}/${slug}/`;
      
      console.log('Switching to Norwegian office (hard navigation):', { englishSlug: slug, targetPath });
      // Use hard navigation to ensure clean page load
      window.location.href = targetPath;
      setIsChangingLanguage(false);
      return;
    }
    
    // Check if on old office page structure and redirect to new structure
    const oldNorwegianOfficeMatch = path.match(/^\/kontor\/(.+)$/);
    const oldEnglishOfficeMatch = path.match(/^\/offices\/(.+)$/);
    
    if (oldNorwegianOfficeMatch || oldEnglishOfficeMatch) {
      // For old office URLs, redirect to archive page instead of trying to maintain the office page
      const targetPath = getArchivePath(newLocale);
      try {
        await router.push(targetPath, targetPath, {
          locale: newLocale,
          scroll: false,
          shallow: true
        });
        await refetch({
          headerLocation: 'PRIMARY',
          language: newLocale.toUpperCase()
        });
        window.dispatchEvent(new CustomEvent('languageChange', { detail: { newLocale } }));
      } catch (error) {
        console.error('Error switching language:', error);
      } finally {
        setIsChangingLanguage(false);
      }
      return;
    }
    
    // Default: just switch locale, keep path
    try {
      await router.push(asPath, asPath, { locale: newLocale, scroll: false, shallow: false });
      await refetch({
        headerLocation: 'PRIMARY',
        language: newLocale.toUpperCase()
      });
      window.dispatchEvent(new CustomEvent('languageChange', { detail: { newLocale } }));
    } catch (error) {
      console.error('Error switching language:', error);
    } finally {
      setIsChangingLanguage(false);
    }
  };

  const renderMobileMenuItem = (item) => {
    const hasChildren = item.childItems?.nodes?.length > 0;
    const isExpanded = expandedItems.has(item.id);

    return (
      <li key={item.id} className={cx('mobile-menu-item', { 'has-children': hasChildren })}>
        {hasChildren ? (
          <>
            <Link 
              href={item.path}
              onClick={() => setIsNavShown(false)}
            >
              {item.label}
            </Link>
            <button 
              onClick={(e) => {
                e.preventDefault();
                handleItemToggle(item.id);
              }}
              className={cx('expand-button', { 'expanded': isExpanded })}
              aria-label={`Toggle ${item.label} submenu`}
            >
              <svg 
                width="12" 
                height="12" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <ul className={cx('submenu', { 'expanded': isExpanded })}>
              {item.childItems.nodes.map(child => {
                let childPath = child.path;
                
                // Transform område paths for mobile menu too
                if (childPath) {
                  const omradeMatch = childPath.match(/^\/([^\/]+)\/?$/);
                  
                  if (omradeMatch && !childPath.includes('/kontorer') && !childPath.includes('/offices') && !childPath.includes('/kontakt')) {
                    const slug = omradeMatch[1];
                    const commonPageSlugs = ['kontakt-oss', 'contact-us', 'om-oss', 'about-us', 'kontakt', 'contact'];
                    
                    if (!commonPageSlugs.includes(slug)) {
                      if (router.locale === 'en') {
                        childPath = `/en/offices/for-rent/${slug}/`;
                      } else {
                        childPath = `/kontorer/til-leie/${slug}/`;
                      }
                    }
                  }
                }
                
                const isEnglishChildOmrade = childPath?.includes('/en/offices/for-rent/');
                
                return (
                  <li key={child.id} className={cx('mobile-menu-item')}>
                    {isEnglishChildOmrade ? (
                      <a href={childPath} onClick={() => setIsNavShown(false)}>
                        {child.label}
                      </a>
                    ) : (
                      <Link 
                        href={childPath} 
                        onClick={() => setIsNavShown(false)}
                        locale={false}
                        scroll={true}
                      >
                        {child.label}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          (() => {
            let itemPath = item.path;
            
            // Transform område paths for mobile menu
            if (itemPath) {
              const omradeMatch = itemPath.match(/^\/([^\/]+)\/?$/);
              
              if (omradeMatch && !itemPath.includes('/kontorer') && !itemPath.includes('/offices') && !itemPath.includes('/kontakt')) {
                const slug = omradeMatch[1];
                const commonPageSlugs = ['kontakt-oss', 'contact-us', 'om-oss', 'about-us', 'kontakt', 'contact'];
                
                if (!commonPageSlugs.includes(slug)) {
                  if (router.locale === 'en') {
                    itemPath = `/en/offices/for-rent/${slug}/`;
                  } else {
                    itemPath = `/kontorer/til-leie/${slug}/`;
                  }
                }
              }
            }
            
            const isEnglishItemOmrade = itemPath?.includes('/en/offices/for-rent/');
            
            return isEnglishItemOmrade ? (
              <a href={itemPath} onClick={() => setIsNavShown(false)}>
                {item.label}
              </a>
            ) : (
              <Link 
                href={itemPath} 
                onClick={() => setIsNavShown(false)}
                locale={false}
                scroll={true}
              >
                {item.label}
              </Link>
            );
          })()
        )}
      </li>
    );
  };

  const hierarchicalMenuItems = flatListToHierarchical(menuItems);

  return (
    <header className={cx('component', { 'nav-open': isNavShown })}>
      <nav className={cx('navbar')}>
        <Link href="/" className={cx('logo-wrapper')}>
          {currentLogo?.mediaItemUrl ? (
            <Image 
              src={getOptimizedImageUrl(currentLogo.mediaItemUrl)}
              alt={currentLogo.altText || 'Spacefinder Logo'}
              width={220} 
              height={50} 
              priority
              loading="eager"
              style={{ 
                width: 'auto', 
                height: 'auto',
                maxWidth: '220px',
                maxHeight: '50px'
              }}
              sizes="(max-width: 1024px) 180px, 220px"
            />
          ) : (
            // Placeholder to maintain layout during loading
            <div 
              style={{ 
                width: '220px', 
                height: '50px', 
                background: 'transparent' 
              }}
              aria-label="Loading logo"
            />
          )}
        </Link>
        <NavigationMenu
          className={cx('main-navigation')}
          menuItems={menuItems}
        />
        <div className={cx('header-actions')}>
          <ClientOnly fallback={<div className="w-12 h-6 bg-gray-200 rounded-full"></div>}>
            <ThemeToggle />
          </ClientOnly>
          <button 
            className={cx('language-switcher', { 'disabled': isChangingLanguage })}
            onClick={handleLanguageSwitch}
            disabled={isChangingLanguage}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>{activeLocale === 'nb' ? 'English' : 'Norsk'}</span>
          </button>
          <Link href="/kontakt-oss" className={cx('cta-button')}>
            {activeLocale === 'nb' ? 'Kontakt oss' : 'Contact us'}
          </Link>
        </div>

        {/* Mobile Theme Toggle */}
        <div className={cx('mobile-theme-toggle')}>
          <ClientOnly fallback={<div className="w-12 h-6 bg-gray-200 rounded-full"></div>}>
            <ThemeToggle />
          </ClientOnly>
        </div>

        <button 
          className={cx('nav-toggle')} 
          onClick={() => setIsNavShown(!isNavShown)}
          aria-label="Toggle mobile menu"
        >
          <div className={cx('burger', { 'active': isNavShown })}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>
      </nav>

      {/* Mobile Navigation Overlay */}
      <div className={cx('mobile-nav', { 'active': isNavShown })}>
        <div className={cx('mobile-nav-content')}>
          <ul className={cx('mobile-menu')}>
            {hierarchicalMenuItems.map(item => renderMobileMenuItem(item))}
          </ul>
          <div className={cx('mobile-actions')}>
            <button 
              className={cx('language-switcher', { 'disabled': isChangingLanguage })}
              onClick={handleLanguageSwitch}
              disabled={isChangingLanguage}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M2 12H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{activeLocale === 'nb' ? 'English' : 'Norsk'}</span>
            </button>
            <Link href="/kontakt-oss" className={cx('cta-button')}>
              {activeLocale === 'nb' ? 'Kontakt oss' : 'Contact us'}
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
